import type { ReactNode } from 'react';
import { PermissionGate } from '../auth/PermissionGate';
import { Tooltip } from './Tooltip';

/*
  ONE TREATMENT, AND IT IS THE BRAND'S OWN PALE YELLOW (03 Nov 2026) — the same call that re-tinted
  `<ProfileBanner>` the same day, extended here because "all" meant both strips.

  There were six tones and `tone` is GONE rather than ignored, for `<ProfileBanner>`'s reason:
  a prop accepted and never read is a dead field. The TITLE says what is waiting, the
  description says what to do about it, and the CTA is the thing to press — the tint was a
  fourth telling, and because every alert carried one, "1 nominee" sat in the same blue as a
  genuine blocker.

  **What is given up, deliberately**: the index page's prerequisites banner is no longer amber,
  and an empty `<ProfileCard>`'s alert no longer differs from a waiting one. Both still say so
  in words, and the prerequisites banner additionally disables the Create it is about.

  The raised edge is what now separates it from the panel — the same inset highlight and drop
  shadow `<ProfileBanner>` wears, so the two strips read as one family. Written LITERALLY: a
  class built from a template literal generates no CSS and fails silently.
*/
const RAISED =
  'shadow-[inset_0_1px_0_0_rgb(255_255_255_/_0.9),0_1px_2px_0_rgb(15_23_42_/_0.08),0_2px_4px_-1px_rgb(15_23_42_/_0.06)]';

/*
  SUBTLE YELLOW, BLACK LABEL (03 Nov 2026, product call) — REPLACING the grey of the same day.

  Grey was the right correction to six competing tones and the wrong resting state: flattened,
  the strip receded so far into the panel it was sitting on that the raised edge was doing all
  the work of saying it was there at all. Brand yellow at its LIGHTEST step is the product's own
  chrome, which is what this strip is: it is the shell saying something, not the record.

  THE 'NEVER ON A RECORD'S STATE' RULE IS NARROWED, NOT BROKEN. A status PILL reports state and
  stays off the brand ramp. This strip reports what the SHELL wants next, and it carries exactly
  one tint for every one of those — there is still no ramp to read a severity off.

  `brand-50` (#fefbea) with a `brand-200` edge, and the text is `slate-900`: at 17.2:1 the
  label is the strongest thing on the strip, which is the point of a black font on a pale tint.
  (This is the one place content reaches 900 — the selection tone is otherwise reserved, and
  the trade is deliberate: the strip has no selected thing to be confused with.)

  Written LITERALLY: a Tailwind class built from a template literal generates no CSS and fails
  silently.
*/
const ALERT = `bg-brand-50 text-slate-900 ring-brand-200 ${RAISED}`;

/*
  THE ONE CARVE-OUT: a FAILURE is red (03 Nov 2026, same day, after the flattening).

  `error` is a BOOLEAN, not a tone — deliberately, because the six-tone prop is what was just
  removed and a ramp would grow back. There is exactly one state that earns a colour here, and
  naming it as a yes/no means no call site can reach for "amber-ish" again.

  **Why it earns the exception**: flattened, an unreachable server read exactly like a routine
  instruction — the Award profile's "Could not load this award" sat in the same grey as "each
  nominee is asked for 2 documents", and the WORDS were the only difference. Red for destructive
  or invalid is a rule the product already keeps everywhere else; this restores it here alone.

  It is the product's own `red-50`/`red-200`/`red-800`, not a new value — the same red a
  destructive confirm and an invalid field already wear.
*/
const ALERT_ERROR = `bg-red-50 text-red-800 ring-red-200 ${RAISED}`;

/**
 * The glyph's own disc, a step deeper than the banner's ground.
 *
 * Product call, 29 Sep 2026: *use this banner template for profile pages that have suggest
 * actions.* The glyph was drawn bare, so on a tinted strip it read as a mark somebody had left
 * beside the text rather than as the banner's own icon — the disc is what makes it the subject.
 *
 * ONE step, never two: at `-200` it would match the ring and the banner would read as a box
 * with a second box in it. `-100` sits between the ground and the edge, which is the same
 * three-surface reasoning the folder tabs run on.
 */
/* One step in from the ground, as it always was — now on the brand ramp the ground moved to. */
const DISC = 'bg-brand-100';

/** One step deeper than the error ground, as the neutral disc is to the neutral ground. */
const DISC_ERROR = 'bg-red-100';

/**
 * Persistent profile status banner — reference §7 and §14.
 *
 * Rendered for **every** lifecycle state, never `return null`. A page that hides the banner
 * when nothing is actionable teaches the user to ignore the region entirely; "waiting" and
 * "finished" are states worth stating.
 *
 * Tones: blue in-progress · emerald good-to-go · amber attention · slate neutral-terminal ·
 * red rejected/blocked.
 */
export function NextActionAlert({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  action,
  permission,
  actionPermission,
  actionDisabledReason = null,
  onDismiss,
  error = false,
  id,
}: {
  icon: ReactNode;
  title: string;
  description?: ReactNode;
  /** The default CTA renders only when BOTH label and handler are set. */
  actionLabel?: string;
  onAction?: () => void;
  /** A custom CTA, which overrides the default button. */
  action?: ReactNode;
  /**
   * Gates the WHOLE banner. Usually the wrong choice — prefer `actionPermission`, so a
   * read-only viewer can still see what state the record is in.
   */
  permission?: string;
  /** Gates just the default CTA. The banner and its state stay visible to everyone. */
  actionPermission?: string;
  /**
   * DISABLES the default CTA for STATE, saying why in a `<Tooltip>` — the permission HIDES, this
   * disables. `<ProfileCard>` passes its `editDisabledReason`, so a banner offering the card's Edit
   * cannot open a form the card's own cog refuses.
   */
  actionDisabledReason?: string | null;
  onDismiss?: () => void;
  /** The strip is reporting a FAILURE — the one state that is not grey. */
  error?: boolean;
  id?: string;
}) {
  const body = (
    <div
      id={id}
      role="status"
      className={`flex items-start gap-3 rounded-panel px-4 py-3 ring-1 ${error ? ALERT_ERROR : ALERT}`}
    >
      {/*
        THE GLYPH IN A DISC — 36px, centred, one step deeper than the ground. `shrink-0`
        because the banner is a flex row and the description beside it is the part that wraps;
        without it a long sentence squeezes the disc into an oval.

        `slate-200`, not `slate-100`: the ground IS `slate-50`, so one step from it is 100 and
        that is invisible.
      */}
      <span
        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${error ? DISC_ERROR : DISC}`}
        aria-hidden="true"
      >
        {icon}
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        {description ? <p className="mt-0.5 text-xs opacity-90">{description}</p> : null}
      </div>

      {action ?? (actionLabel && onAction ? (
        <PermissionGate permission={actionPermission}>
          {/* Wrapped only when there is something to say — the other 27 callers keep their bare button. */}
          {actionDisabledReason != null ? (
            <Tooltip disabledReason={actionDisabledReason}>
              <button
                type="button"
                id={id ? `${id}-action` : undefined}
                disabled
                className="shrink-0 rounded-lg bg-slate-200 px-3 py-1.5 text-xs font-medium text-slate-400 ring-1 ring-slate-200"
              >
                {actionLabel}
              </button>
            </Tooltip>
          ) : (
            <button
              type="button"
              id={id ? `${id}-action` : undefined}
              onClick={onAction}
              className="shrink-0 rounded-lg bg-white/80 px-3 py-1.5 text-xs font-medium ring-1 ring-current/20 transition hover:bg-white"
            >
              {actionLabel}
            </button>
          )}
        </PermissionGate>
      ) : null)}

      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded p-1 opacity-60 transition hover:opacity-100"
        >
          ✕
        </button>
      ) : null}
    </div>
  );

  return permission ? <PermissionGate permission={permission}>{body}</PermissionGate> : body;
}
